import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Check, Users2, ShieldCheck, Lock, Headphones, Home, Calendar, KeyRound,
    Smartphone, StickyNote, Camera, FileText, Search, ChevronDown, ArrowRight,
    Sparkles, Loader2,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WaitlistDialog from "../components/WaitlistDialog";
import Pricing, { ExampleCalc } from "../components/Pricing";
import ROIStrip from "../components/ROIStrip";
import { submitContact } from "../lib/api";
import { readMarketingConsent, readMetaCookies } from "../lib/consent";

const DK_PHONE = /^(\+?45[ -]?)?(\d{2}[ -]?){3}\d{2}$/;

// Track events fired at least once during this page load. Module-level so
// client-side navigation away and back to /beregn-pris does not refire.
// A hard refresh reloads the module and resets the set — expected behavior.
const firedEvents = new Set();

function pushDL(payload) {
    if (firedEvents.has(payload.event)) return;
    firedEvents.add(payload.event);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
}

function generateEventId() {
    const ts = Math.floor(Date.now() / 1000);
    const rand = Math.random().toString(36).slice(2, 6).padEnd(4, "0");
    return `lead_${ts}_${rand}`;
}

// Shared ref for the latest calculator total, mutated by ExampleCalc's
// onChange and read by TrialForm on successful submit.
const calcTotalRef = { current: null };

const PROOF_ITEMS = [
    {
        h: "Bygget af folk der gør rent",
        p: "Udviklet i et dansk rengøringsfirma med 150 ansatte, der bruger det hver dag.",
        Icon: Users2,
    },
    {
        h: "Ingen binding",
        p: "Opsig når I vil. Jeres data kan altid eksporteres gratis.",
        Icon: Lock,
    },
    {
        h: "Data i Danmark og EU",
        p: "GDPR, kryptering og daglig backup med 30 dages historik.",
        Icon: ShieldCheck,
    },
    {
        h: "Support på dansk",
        p: "Fredericia, ikke et callcenter i en anden tidszone.",
        Icon: Headphones,
    },
];

const CORE_INCLUDES = [
    { Icon: Home,       t: "Kunder og kontaktpersoner samlet ét sted" },
    { Icon: Calendar,   t: "Kalender, opgaver og ruter" },
    { Icon: KeyRound,   t: "Brugere og rettigheder" },
    { Icon: Smartphone, t: "Mobil-app med tjek ind og tjek ud" },
    { Icon: StickyNote, t: "Noter og journal på hver kunde" },
    { Icon: Camera,     t: "Fotodokumentation fra opgaven" },
    { Icon: FileText,   t: "Dokumenter og filer" },
    { Icon: Search,     t: "Dashboard og søgning på tværs" },
];

const FAQ = [
    { q: "Hvad koster de 14 dage?",
      a: "Ingenting. I skal ikke bruge kreditkort, og prøveperioden stopper af sig selv, hvis I ikke siger andet." },
    { q: "Er der binding bagefter?",
      a: "Nej. I betaler måned for måned og kan opsige når som helst. Jeres data kan altid eksporteres gratis, så I står aldrig fast." },
    { q: "Hvor lang tid går der, før vi kører på det?",
      a: "Typisk under en uge. Vi flytter data ind, sætter modulerne op efter jeres behov, og så kører I live." },
    { q: "Kan vi starte småt og slå moduler til senere?",
      a: "Ja. De fleste starter med Core og drift og kvalitet, og slår nøgler eller økonomi til når det første er kørt ind." },
    { q: "Hvad sker der, når vi bliver flere medarbejdere?",
      a: "I betaler pr. medarbejder ud over dem der er med i grundprisen. Der er ingen ny opstart og ingen ny aftale." },
    { q: "Hvor ligger vores data?",
      a: "På servere i EU og EØS, krypteret, med daglig backup og 30 dages historik. Ingen udenlandske mellemled." },
];

function useDocumentTitle(title, description) {
    useEffect(() => {
        document.title = title;
        const meta = document.querySelector("meta[name='description']")
            || (() => { const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m; })();
        meta.setAttribute("content", description);
        // noindex — this page is for paid traffic only
        const robots = document.querySelector("meta[name='robots']")
            || (() => { const m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); return m; })();
        robots.setAttribute("content", "noindex, nofollow");
        return () => { robots.setAttribute("content", "index, follow"); };
    }, [title, description]);
}

function TrialForm() {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Skriv jeres navn";
        if (!form.company.trim()) e.company = "Skriv firmanavn";
        if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Skriv en gyldig e-mail";
        if (!form.phone.trim() || !DK_PHONE.test(form.phone.replace(/\s/g, ""))) e.phone = "Skriv et dansk telefonnummer";
        if (!consent) e.consent = "Bekræft venligst";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        const eventId = generateEventId();
        const marketingConsent = readMarketingConsent();
        const { fbp, fbc } = readMetaCookies();
        try {
            await submitContact({
                ...form,
                message: "14-dages gratis prøveperiode — startet via /beregn-pris",
                event_id: eventId,
                marketing_consent: marketingConsent,
                fbp,
                fbc,
            });
            setDone(true);
            // Server confirmed success — fire form_submit with the same event_id
            // that was sent to the backend (for Meta CAPI dedup later).
            pushDL({
                event: "form_submit",
                event_id: eventId,
                calculated_price: calcTotalRef.current,
            });
            // Legacy PostHog conversion event (kept — does not touch dataLayer).
            if (typeof window !== "undefined" && window.posthog) {
                try { window.posthog.capture("trial_signup", { source: "beregn-pris" }); } catch (e) { /* ignore */ }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Noget gik galt. Prøv igen.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="text-center" data-testid="trial-form-success">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="h-7 w-7 text-emerald-600" strokeWidth={3} />
                </div>
                <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold text-slate-900">Tak. Vi er i gang.</h3>
                <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
                    I hører fra os inden for én arbejdsdag. Vi ringer kort, opretter kontoen
                    og flytter jeres data ind, så I kan bruge de 14 dage på systemet i
                    stedet for på opsætning.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="trial-form">
            <div className="grid md:grid-cols-2 gap-4">
                {[
                    { k: "name", label: "Navn", type: "text", auto: "name" },
                    { k: "company", label: "Firma", type: "text", auto: "organization" },
                    { k: "email", label: "E-mail", type: "email", auto: "email" },
                    { k: "phone", label: "Telefon", type: "tel", auto: "tel" },
                ].map((f) => (
                    <label key={f.k} className="block">
                        <span className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</span>
                        <input
                            type={f.type}
                            autoComplete={f.auto}
                            value={form[f.k]}
                            onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                            onFocus={() => pushDL({ event: "form_start" })}
                            data-testid={`trial-${f.k}`}
                            className={`w-full rounded-lg border px-4 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors ${
                                errors[f.k] ? "border-rose-400 bg-rose-50" : "border-slate-200"
                            }`}
                        />
                        {errors[f.k] && <span className="text-xs text-rose-600 mt-1 block">{errors[f.k]}</span>}
                    </label>
                ))}
            </div>

            <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="trial-consent"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <span>
                    Ja tak, I må kontakte mig om opstart. Jeg kan altid framelde mig.{" "}
                    <Link to="/privatlivspolitik" className="text-sky-700 hover:underline">
                        Se privatlivspolitik
                    </Link>.
                </span>
            </label>
            {errors.consent && <span className="text-xs text-rose-600">{errors.consent}</span>}

            <button
                type="submit"
                disabled={submitting}
                data-testid="trial-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold h-12 px-6 shadow-xl shadow-sky-600/25 transition-all active:scale-[0.98] disabled:opacity-60"
            >
                {submitting
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Sender…</>
                    : <>Start 14 dage gratis <ArrowRight className="h-4 w-4" /></>}
            </button>

            <div className="pt-2 grid grid-cols-3 gap-3 text-[11px] text-slate-500">
                {[
                    "Intet kreditkort",
                    "Ingen binding",
                    "Klar til brug på under en uge",
                ].map((t) => (
                    <div key={t} className="flex items-center gap-1.5 justify-center text-center">
                        <Check className="h-3 w-3 text-emerald-500 shrink-0" /> {t}
                    </div>
                ))}
            </div>
        </form>
    );
}

function FaqAccordion() {
    const [open, setOpen] = useState(0);
    return (
        <div className="max-w-3xl mx-auto space-y-2" data-testid="beregn-faq">
            {FAQ.map((item, i) => {
                const isOpen = open === i;
                return (
                    <div key={item.q} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setOpen(isOpen ? -1 : i)}
                            className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-slate-50 transition-colors"
                        >
                            <span className="font-semibold text-slate-900">{item.q}</span>
                            <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isOpen && (
                            <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{item.a}</div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function BeregnPrisPage() {
    useDocumentTitle(
        "Se hvad Cleany koster for jeres firma",
        "Sæt antal medarbejdere og de moduler I har brug for, og se prisen med det samme. Prøv derefter Cleany gratis i 14 dage. Ingen binding, dansk support, data i EU.",
    );

    const [waitlistOpen, setWaitlistOpen] = useState(false);
    const calcRef = useRef(null);
    const formRef = useRef(null);
    const [showStickyBar, setShowStickyBar] = useState(false);

    // Mobile sticky bar — visible while calculator is in viewport and form not yet reached
    useEffect(() => {
        const calc = calcRef.current;
        const form = formRef.current;
        if (!calc || !form) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.target === calc) setShowStickyBar(e.isIntersecting);
                    if (e.target === form && e.isIntersecting) setShowStickyBar(false);
                });
            },
            { rootMargin: "-100px 0px -100px 0px" },
        );
        io.observe(calc);
        io.observe(form);
        return () => io.disconnect();
    }, []);

    // calculator_view — fired once when the calculator is ≥50% visible for ≥1s
    useEffect(() => {
        const el = calcRef.current;
        if (!el) return;
        let dwellTimer = null;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        if (dwellTimer == null) {
                            dwellTimer = setTimeout(() => {
                                pushDL({ event: "calculator_view" });
                                io.disconnect();
                            }, 1000);
                        }
                    } else if (dwellTimer != null) {
                        clearTimeout(dwellTimer);
                        dwellTimer = null;
                    }
                });
            },
            { threshold: [0.5] },
        );
        io.observe(el);
        return () => {
            if (dwellTimer != null) clearTimeout(dwellTimer);
            io.disconnect();
        };
    }, []);

    // Handler wired into ExampleCalc: fires on every user-driven state change.
    // Fires `calculator_interact` at most once (via pushDL's fired-set) and
    // always keeps `calcTotalRef.current` up to date for form_submit.
    const onCalcChange = (currentTotal) => {
        calcTotalRef.current = currentTotal;
        pushDL({ event: "calculator_interact", calculated_price: currentTotal });
    };

    const scrollTo = (el) => el?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <div className="min-h-screen bg-white">
            <Navbar onCtaClick={() => setWaitlistOpen(true)} />

            <main className="pt-24 pb-24">
                {/* Section 1: Hero (centered) */}
                <section className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> Prisberegner
                    </div>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                        Se hvad Cleany koster for{" "}
                        <span className="text-sky-600">jeres firma.</span>
                    </h1>
                    <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto">
                        Sæt antal medarbejdere og de moduler I har brug for. Så har I prisen med det samme, uden at tale med en sælger.
                    </p>
                    <div className="mt-8">
                        <button
                            type="button"
                            onClick={() => scrollTo(calcRef.current)}
                            data-testid="hero-scroll-calc"
                            className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold px-8 h-12 shadow-xl shadow-sky-600/25 transition-all active:scale-[0.97]"
                        >
                            Beregn min pris
                            <ArrowRight className="h-4 w-4" />
                        </button>
                        <p className="mt-3 text-xs text-slate-500">Tager 30 sekunder. Ingen kontaktoplysninger.</p>
                    </div>
                </section>

                {/* Section 2: Calculator (reused) */}
                <section ref={calcRef} className="max-w-5xl mx-auto px-6 lg:px-10 mt-14 scroll-mt-24">
                    <ExampleCalc onChange={onCalcChange} />
                    <p className="mt-3 text-center text-xs text-slate-500">
                        Prisen er vejledende og uden moms. Ingen binding.
                    </p>
                </section>

                {/* Section 3: Trial opt-in */}
                <section ref={formRef} className="mt-16 px-6 lg:px-10 scroll-mt-24">
                    <div className="max-w-2xl mx-auto rounded-2xl bg-white p-8 md:p-10 shadow-2xl shadow-sky-900/10 border-2 border-sky-200 ring-1 ring-sky-100">
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> 14 dage gratis
                            </div>
                            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                                Prøv Cleany gratis i 14 dage
                            </h2>
                            <p className="mt-3 text-slate-600 leading-relaxed">
                                Vi opretter jeres konto og flytter jeres eksisterende lister ind. I skal ikke bruge et kreditkort, og der er ingen binding.
                            </p>
                        </div>
                        <TrialForm />
                    </div>
                </section>

                {/* Section 3b: Full pricing (module explanations) — informational, for interested visitors */}
                <div className="mt-8">
                    <Pricing hideCalculator onCtaClick={() => scrollTo(formRef.current)} />
                </div>

                {/* Section 3c: ROI / concrete value — for visitors who want to see the savings breakdown */}
                <div className="mt-4">
                    <ROIStrip onCtaClick={() => scrollTo(formRef.current)} />
                </div>

                {/* Section 4: Proof strip */}
                <section className="mt-20 px-6 lg:px-10">
                    <div className="max-w-6xl mx-auto rounded-2xl bg-slate-50 border border-slate-200 p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-4 md:divide-x md:divide-slate-200 gap-6 md:gap-0">
                            {PROOF_ITEMS.map((item) => {
                                const Icon = item.Icon;
                                return (
                                    <div key={item.h} className="md:px-6 flex flex-col items-start gap-2">
                                        <Icon className="h-5 w-5 text-sky-600" />
                                        <div className="font-semibold text-slate-900 text-sm">{item.h}</div>
                                        <div className="text-sm text-slate-600 leading-relaxed">{item.p}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Section 5: What the price includes */}
                <section className="mt-20 px-6 lg:px-10 text-center">
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                        Hvad prisen dækker
                    </h2>
                    <p className="mt-3 text-slate-600 max-w-xl mx-auto">
                        Cleany Core er fundamentet. Alt herunder er med, uanset hvilke moduler I vælger.
                    </p>
                    <div className="mt-10 max-w-3xl mx-auto grid md:grid-cols-2 gap-x-8 gap-y-4 text-left">
                        {CORE_INCLUDES.map((it) => {
                            const Icon = it.Icon;
                            return (
                                <div key={it.t} className="flex items-start gap-3">
                                    <div className="h-8 w-8 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
                                        <Icon className="h-4 w-4 text-sky-600" />
                                    </div>
                                    <div className="text-slate-700 pt-1.5 text-sm">{it.t}</div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Section 6: FAQ */}
                <section className="mt-20 px-6 lg:px-10">
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight text-center">
                        Det I plejer at spørge om
                    </h2>
                    <div className="mt-10">
                        <FaqAccordion />
                    </div>
                </section>

                {/* Section 7: Closing dark block */}
                <section className="mt-20 px-6 lg:px-10">
                    <div className="max-w-4xl mx-auto rounded-2xl bg-gradient-to-br from-slate-900 to-sky-950 p-10 md:p-14 text-center text-white relative overflow-hidden">
                        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" aria-hidden="true" />
                        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-cyan-400/15 blur-3xl" aria-hidden="true" />
                        <div className="relative">
                            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[11px] font-semibold text-sky-200">
                                <Sparkles className="h-3 w-3" /> Klar til at starte
                            </div>
                            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight">
                                Klar til at prøve det på jeres egen drift?
                            </h2>
                            <p className="mt-4 text-sky-100 max-w-lg mx-auto leading-relaxed">
                                14 dage, intet kreditkort, ingen binding. Vi flytter jeres lister ind, så I kan bruge tiden på systemet i stedet for på opsætning.
                            </p>
                            <button
                                type="button"
                                onClick={() => scrollTo(formRef.current)}
                                data-testid="closing-cta"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-white font-semibold px-8 h-12 shadow-xl shadow-sky-900/30 transition-all active:scale-[0.97]"
                            >
                                Start 14 dage gratis
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <p className="mt-4 text-xs text-sky-200/70">
                                Vil I hellere se det først? Ring på{" "}
                                <a href="tel:+4570221990" className="underline hover:text-white">70 22 19 90</a>.
                            </p>
                        </div>
                    </div>
                </section>
            </main>

            {/* Mobile sticky bar */}
            {showStickyBar && (
                <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 p-3 shadow-2xl"
                     data-testid="beregn-sticky-bar">
                    <button
                        type="button"
                        onClick={() => scrollTo(formRef.current)}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold h-11 text-sm active:scale-[0.98] transition-all"
                    >
                        Start 14 dage gratis
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            )}

            <WaitlistDialog open={waitlistOpen} onClose={() => setWaitlistOpen(false)} source="beregn-pris" />
            <Footer onCtaClick={() => setWaitlistOpen(true)} />
        </div>
    );
}
